import React, { useEffect, useState } from 'react';
import logo from "../../src/assets/images/willowyardlogo.png";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginValidation } from '../helper/Message';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { userService } from '../services/user.service';
import { actions } from '../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers';

export default function LoginForm() {
    const navigate = useNavigate();
    const { token } = useSelector((e: RootState) => ({ token: e.token }))
    const dispatch = useDispatch();
    const { login } = actions

    const [visible, setVisible] = useState(false);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [privacyConsent, setPrivacyConsent] = useState(false);


    const validationSchema = yup.object().shape({
        password: yup.string().trim().required(loginValidation.passwordRequired),
        email: yup.string().trim().required(loginValidation.emailRequired)
    });

    const { register: loginCredentials, handleSubmit, watch, formState: { errors } } = useForm({ resolver: yupResolver(validationSchema) });
    const email = watch('email');
    const password = watch('password');

    const isFormFilled = email?.trim() && password?.trim() && privacyConsent;

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            setError('');
            const response = await userService.login({
                email: data.email,
                password: data.password,
                privacyConsent
            });
            // Store the token
            dispatch(login(response.token, response.user))

            navigate('/dashboard');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { token !== '' && navigate('/dashboard') }, [token])
    return (
        <div>
            <div className="w-full max-w-[500px] space-y-5">
                <img src={logo} alt="" className="py-4" />
                <h2 className="text-green text-[25px] sm:text-[28px] md:text-[30px] xl:text-[32px] font-[Geist-Medium]">Login</h2>

                {
                    error &&
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">{error}</span>
                    </div>
                }

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className='w-full'>
                        <input
                            type="text"
                            placeholder="Email"
                            className="input-css"
                            {...loginCredentials("email")}
                        />
                        {errors?.email && <p className='error'>{errors?.email.message}</p>}
                    </div>

                    <div className='w-full relative'>
                        <input
                            type={visible ? "text" : "password"}
                            placeholder="Password"
                            className="input-css"
                            {...loginCredentials("password")}
                        />
                        <span
                            className="absolute top-4 right-6 cursor-pointer"
                            onClick={() => setVisible((prevVisible) => !prevVisible)}
                        >
                            {
                                visible ?
                                    <Icon icon="codicon:eye" className="icon-color" width={20} height={20} />
                                    :
                                    <Icon icon="codicon:eye-closed" className="icon-color" width={20} height={20} />
                            }
                        </span>
                        {errors?.password && <p className='error'>{errors?.password.message}</p>}
                    </div>

                    {/* Consent Checkboxes */}
                    <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                id="privacyConsent"
                                checked={privacyConsent}
                                onChange={(e) => setPrivacyConsent(e.target.checked)}
                                className="mt-1 h-4 w-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                                required
                            />
                            <label htmlFor="privacyConsent" className="text-sm text-gray-700 leading-5">
                                I agree to the{' '}
                                <a
                                    href="/privacy-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green hover:text-green-600 underline"
                                >
                                    Privacy Policy
                                </a>
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                        </div>
                    </div>

                    <div className='py-5'>
                        <button
                            type="submit"
                            disabled={loading || !isFormFilled}
                            className={`w-full  text-white py-3 rounded-md font-[Geist-semibold] text-sm sm:text-base md:text-lg disabled:opacity-50
                                  ${!isFormFilled || loading ? 'bg-[#BFBFBF] cursor-not-allowed' : 'bg-green hover:bg-green'}`}>
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                    </div>
                </form>

                <div className="space-y-2 text-sm md:text-base text-black-2  text-center font-[Geist-Medium]">
                    Stuck or have questions? Our support team is here to help!
                    <a 
                        href="mailto:support@willow-technologies.com"
                        className="text-green font-[Geist-Bold] cursor-pointer hover:text-green-600 underline"
                    >
                        support@willow-technologies.com
                    </a>
                </div>
            </div>
        </div>
    );
};